import { useState } from 'react';
import { toast } from 'react-toastify';

import { NtfyMessageLevel, TotadminPermission, usePublishNtfyTestMessageMutation } from 'data/__generated__';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { TotemTextArea } from 'components/TotemTextArea';

const PUBLISH_PERMISSIONS = [TotadminPermission.NtfyManage];

const LEVEL_OPTIONS = [
    { value: NtfyMessageLevel.Debug, label: 'Debug' },
    { value: NtfyMessageLevel.Info, label: 'Info' },
    { value: NtfyMessageLevel.Warning, label: 'Warning' },
    { value: NtfyMessageLevel.Error, label: 'Error' },
];

export const PublishNtfyTestMessageButton = ({
    publishableTopics,
    onPublished,
}: {
    publishableTopics: string[];
    onPublished: () => void;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [topic, setTopic] = useState<string | null>(null);
    const [level, setLevel] = useState<NtfyMessageLevel | null>(NtfyMessageLevel.Info);
    const [message, setMessage] = useState('');

    const [publishNtfyTestMessage, { loading }] = usePublishNtfyTestMessageMutation();

    const selectedTopic = topic ?? publishableTopics[0] ?? null;
    const canSubmit = !!selectedTopic && !!level && !!message.trim() && !loading;

    const onSubmit = async (): Promise<void> => {
        if (!selectedTopic || !level) {
            return;
        }

        const { data } = await publishNtfyTestMessage({
            variables: { topic: selectedTopic, level, message },
            onError: (error) => toast.error(`Impossible de publier le message : ${error.message}`),
        });

        const result = data?.publishNtfyTestMessage;
        if (!result) {
            return;
        }

        if (!result.success) {
            toast.error(`Impossible de publier le message : ${result.error ?? 'erreur inconnue'}`);
            return;
        }

        toast.success(`Message publié sur ${selectedTopic}`);
        setMessage('');
        setIsOpen(false);
        onPublished();
    };

    return (
        <>
            <TotemPrimaryButton
                onClick={() => setIsOpen(true)}
                requiredPermissions={PUBLISH_PERMISSIONS}
                data-test="ntfy-publish-open"
            >
                Envoyer un message de test
            </TotemPrimaryButton>
            <TotemPopup title="Message de test ntfy" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="visible">
                <div className="flex w-[480px] max-w-full flex-col gap-4">
                    <p className="text-sm text-muted-foreground">
                        Publié par le même chemin que les notifications de l&apos;API, avec le canal{' '}
                        <code>totadmin-test</code> dans le titre et ton nom en tag.
                    </p>
                    <TotemSelect<string>
                        label="Topic"
                        value={selectedTopic}
                        onChange={setTopic}
                        options={publishableTopics.map((name) => ({ value: name, label: name }))}
                        dataTest="ntfy-publish-topic"
                    />
                    <TotemSelect<NtfyMessageLevel>
                        label="Niveau"
                        value={level}
                        onChange={setLevel}
                        options={LEVEL_OPTIONS}
                        dataTest="ntfy-publish-level"
                    />
                    <TotemTextArea
                        label="Message"
                        value={message}
                        onChange={setMessage}
                        rows={4}
                        dataTest="ntfy-publish-message"
                    />
                    <TotemPrimaryLoadingButton
                        loading={loading}
                        disabled={!canSubmit}
                        onClick={onSubmit}
                        label="Publier"
                        useIcon={false}
                        requiredPermissions={PUBLISH_PERMISSIONS}
                        data-test="ntfy-publish-submit"
                    >
                        Publier
                    </TotemPrimaryLoadingButton>
                </div>
            </TotemPopup>
        </>
    );
};
